'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import ServiceCard from '@/app/homepage/components/ServiceCard';
import ProjectCard from '@/app/homepage/components/ProjectCard';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import { rfpService, RfpFormData } from '@/lib/services/rfpService';
import Navigation from '@/components/common/Navigation';

export default function EnergyServicesPage() {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState<RfpFormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    projectType: 'energy',
    message: '',
    servicePage: 'energy',
  });

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));
    
    return () => observer?.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    const result = await rfpService.submitInquiry(formData);
    if (result.success) {
      setSubmitStatus('success');
      setFormData({ name: '', company: '', email: '', phone: '', projectType: 'energy', message: '', servicePage: 'energy' });
    } else {
      setSubmitStatus('error');
      setErrorMessage(result.error || 'Failed to submit inquiry.');
    }
    setIsSubmitting(false);
  };

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center border-b border-border">
        <div className="absolute inset-0">
          <Image 
            src="/assets/images/try_4_-1786128937327.jpg"
            alt="Vermeer directional drill crew working on a grassy jobsite"
            fill
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center">
          <Link 
            href="/homepage" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 reveal"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Back to Home
          </Link>
          
          <div className="mb-6 reveal" style={{ transitionDelay: '100ms' }}>
            <Icon name="BoltIcon" size={64} className="text-primary mx-auto mb-6" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-foreground mb-6 reveal" style={{ transitionDelay: '200ms' }}>
            Energy Sector
            <br />
            <span className="text-gradient-orange">Drilling Services</span>
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-8 reveal" style={{ transitionDelay: '300ms' }}>
            Precision horizontal directional drilling for natural gas pipelines, electrical transmission lines, and oil infrastructure. 
            Powering the East Coast with safe, efficient underground utility installations.
          </p>
          
          <Link
            href="/contact"
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all reveal inline-block"
            style={{ transitionDelay: '400ms' }}
          >
            Request Energy Project Quote
          </Link>
        </div>
      </section>

      {/* Equipment Specifications */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Equipment & Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Advanced Drilling Technology
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Our fleet of specialized equipment handles energy sector projects from small-diameter electrical conduits to large-diameter natural gas transmission lines.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard 
              icon="CogIcon"
              title="Vermeer D40x55 S3 Drill"
              description="Agile drilling system for smaller energy projects in confined spaces and urban environments."
              specs={[
                'Thrust/Pullback: 40,000 lbs',
                'Torque: 4,500 ft-lbs',
                'Pipe diameter: 2" to 12"',
                'Max bore length: 1,500 feet',
                'Ideal for: Electric, gas, and water transmission and distribution lines.'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="ShieldCheckIcon"
              title="Safety & Compliance Systems"
              description="Comprehensive safety equipment and monitoring systems for hazardous energy sector environments."
              specs={[
                'Inadvertent returns and downhole pressure monitoring systems',
                'Electromagnetic locating technology',
                'Vacuum excavation for utility verification',
                'OSHA-compliant safety protocols',
                'Emergency response equipment on-site'
              ]}
              delay={200}
            />
          </div>
        </div>
      </section>

      {/* Services Description */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Services & Capabilities
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Energy Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Comprehensive underground utility solutions for natural gas, electrical, and petroleum infrastructure — delivered safely and efficiently across the East Coast.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ServiceCard 
              icon="CogIcon"
              title="Horizontal Directional Drilling"
              description="Our primary method for trenchless installation of energy pipelines and conduit beneath roads, waterways, wetlands, and environmentally sensitive areas. Using Vermeer and Ditch Witch drilling rigs, we install natural gas transmission and distribution lines, electrical conduit, and petroleum pipelines with bore lengths up to 1,500 feet and pipe diameters from 2&quot; to 24&quot;. HDD eliminates the need for open excavation in critical crossings and protected corridors."
              specs={[
                'Natural gas transmission & distribution lines',
                'Electrical conduit & high-voltage cable installation',
                'River, road & railroad crossings',
                'Pipe diameters: 2" to 24"',
                'Max bore length: 1,500 feet'
              ]}
              delay={100}
            />
            
            <ServiceCard 
              icon="BeakerIcon"
              title="Hydro-Vac Excavation"
              description="Pressurized water and industrial vacuum technology for safe, precise excavation in and around active energy infrastructure. Hydro-vac is essential for potholing and daylighting buried gas lines, electrical conduit, and petroleum pipelines before drilling begins. It eliminates the risk of utility strikes in congested energy corridors and is the safest method for working near high-pressure gas mains, live electrical systems, and hazardous liquid pipelines."
              specs={[
                'Utility potholing near live gas & electric lines',
                'Safe excavation in hazardous environments',
                'Slot trenching around active infrastructure',
                'Debris removal & site cleanup',
                'Supports HDD & open trench operations'
              ]}
              delay={200}
            />
            
            <ServiceCard 
              icon="WrenchIcon"
              title="Open Trench"
              description="Traditional open-cut excavation for energy utility installations where trenchless methods are not feasible or cost-effective. Our crews handle full-depth trenching for gas service lines, electrical duct banks, and petroleum product piping. We follow all OSHA, DOT, and pipeline safety requirements and restore surfaces — pavement, gravel, and landscaping — to pre-construction condition upon project completion."
              specs={[
                'Gas service line & meter set installation',
                'Electrical duct bank construction',
                'Petroleum product piping',
                'OSHA & DOT-compliant safety protocols',
                'Full surface restoration included'
              ]}
              delay={300}
            />
            
            <ServiceCard 
              icon="MapPinIcon"
              title="Utility Locating"
              description="Accurate identification and marking of existing underground utilities before any excavation or drilling begins on energy projects. We use electromagnetic locating equipment and ground-penetrating radar (GPR) to map buried gas, electric, petroleum, and telecommunications lines. Precise locating is critical in energy environments where an undetected utility strike can cause explosions, fires, or service outages affecting thousands of customers."
              specs={[
                'Electromagnetic utility locating',
                'Ground-penetrating radar (GPR)',
                'Pre-construction utility mapping',
                'Conflict identification & reporting',
                'Supports all excavation methods'
              ]}
              delay={400}
            />
          </div>
        </div>
      </section>

      {/* Case Studies - Hidden temporarily */}
      <div className="hidden">
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Project Portfolio
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Energy Sector Case Studies
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Real-world examples of complex energy infrastructure projects completed safely, on time, and within budget.
            </p>
          </div>
          
          <div className="space-y-0">
            <ProjectCard 
              title="Delaware River Natural Gas Pipeline"
              location="Wilmington, DE"
              challenge="Install 36-inch natural gas transmission line beneath Delaware River without disrupting shipping lanes or environmental habitats. Required 200-foot depth and 1,800-foot horizontal bore through mixed geology."
              solution="Deployed Vermeer D330x500 with advanced mud system and real-time navigation. Completed bore in single pull with zero environmental impact. Coordinated with Coast Guard for shipping lane management."
              timeline="Completed in 18 days"
              image="https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=800"
              imageAlt="Large diameter pipeline installation equipment at Delaware River drilling site"
              delay={100}
            />
            
            <ProjectCard 
              title="Marcellus Shale Gas Distribution Network"
              location="Bradford County, PA"
              challenge="Connect 12 natural gas well pads to main transmission line across 5 miles of mountainous terrain. Multiple road crossings, stream crossings, and protected wetlands required trenchless technology."
              solution="Executed 23 separate HDD bores ranging from 400 to 1,200 feet using Vermeer D60x90 rigs. Implemented GPS-guided drilling for precision accuracy. Zero surface disruption in protected areas."
              timeline="Completed in 45 days"
              image="https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=800"
              imageAlt="Mountain terrain drilling operation for natural gas well connections in Pennsylvania"
              delay={200}
            />
            
            <ProjectCard 
              title="Electrical Transmission Line Upgrade"
              location="Atlantic City, NJ"
              challenge="Replace aging underground electrical transmission infrastructure beneath busy casino district without disrupting tourism or gaming operations. Required 24/7 power availability during construction."
              solution="Phased HDD installation of 12-inch conduit system for new 138kV transmission cables. Night operations with sound mitigation. Coordinated with utility for live power transfer. Zero downtime achieved."
              timeline="Completed in 32 days"
              image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800"
              imageAlt="Urban electrical infrastructure drilling in Atlantic City casino district at night"
              delay={300}
            />
            
            <ProjectCard 
              title="Oil Pipeline River Crossing"
              location="Chester, PA"
              challenge="Install 20-inch crude oil pipeline beneath Schuylkill River adjacent to active refinery. Strict environmental regulations and contaminated soil conditions required specialized approach."
              solution="Utilized closed-loop mud recycling system to prevent contamination. Real-time monitoring of drilling fluids. Implemented double-wall containment for environmental protection. Passed all EPA inspections."
              timeline="Completed in 21 days"
              image="https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800"
              imageAlt="River crossing pipeline installation with environmental protection systems near refinery"
              delay={400}
            />
          </div>
        </div>
      </section>
      </div>

      {/* Technical Expertise */}
      <section className="py-20 md:py-32 border-b border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Technical Expertise
            </span>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Energy Sector Specializations
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '100ms' }}>
              <Icon name="FireIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Natural Gas Infrastructure</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Transmission & distribution lines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Well pad connections
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Compressor station tie-ins
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Pipeline integrity upgrades
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '200ms' }}>
              <Icon name="BoltIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Electrical Systems</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  High-voltage transmission lines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Substation interconnections
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Distribution network upgrades
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Renewable energy connections
                </li>
              </ul>
            </div>
            
            <div className="p-8 bg-card border border-border rounded-lg reveal" style={{ transitionDelay: '300ms' }}>
              <Icon name="BeakerIcon" size={40} className="text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-3">Oil & Petroleum</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Crude oil pipelines
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Refined product distribution
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Refinery infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1 h-1 rounded-full bg-primary mt-2" />
                  Tank farm connections
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 reveal">
            Ready to Start Your Energy Project?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 reveal" style={{ transitionDelay: '100ms' }}>
            Contact our energy sector specialists for a detailed project assessment and competitive quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center reveal" style={{ transitionDelay: '200ms' }}>
            <button className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all">
              Get Project Quote
            </button>
            <Link href="/homepage">
              <button className="px-8 py-4 border-2 border-foreground text-foreground rounded-lg font-bold text-lg hover:bg-foreground hover:text-background transition-all">
                View All Services
              </button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* RFP Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 relative">
            <button
              onClick={() => { setShowForm(false); setSubmitStatus('idle'); }}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="XMarkIcon" size={24} className="text-foreground" />
            </button>
            <h2 className="text-2xl font-bold text-foreground mb-2">Request Energy Project Quote</h2>
            <p className="text-muted-foreground mb-6">Fill out the form below and we will respond within 48 hours.</p>
            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="CheckCircleIcon" size={40} className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Inquiry Submitted!</h3>
                <p className="text-muted-foreground mb-4">Thank you! We will contact you within 48 hours.</p>
                <button onClick={() => { setShowForm(false); setSubmitStatus('idle'); }} className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all">Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
                    <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="John Smith" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Company *</label>
                    <input type="text" name="company" required value={formData.company} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Acme Energy Corp" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="john@company.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Phone *</label>
                    <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="(555) 123-4567" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Project Details *</label>
                  <textarea name="message" required rows={4} value={formData.message} onChange={handleChange} className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none" placeholder="Tell us about your energy project requirements, timeline, and location..." />
                </div>
                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <Icon name="ExclamationCircleIcon" size={18} className="text-destructive flex-shrink-0" />
                    <p className="text-sm text-destructive">{errorMessage}</p>
                  </div>
                )}
                <button type="submit" disabled={isSubmitting} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                  {isSubmitting ? (
                    <><svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Submitting...</>
                  ) : 'Submit RFP Inquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </main>
  );
}